import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { AccordionSection, Badge, Button } from '@aimsos/ui';

import FilterConfigRow from './FilterConfigRow.jsx';
import FiltersEmptyState from './FiltersEmptyState.jsx';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

import { Plus } from 'lucide-react';

/**
 * `FILTERS` (CT-5193).
 *
 * Entity path only: a governed dataset already carries its filters. Incomplete
 * rows are allowed to sit here: the prototype lets an empty row
 * through to the Widget step, and the step's validity does not depend on them —
 * they are simply excluded from the preview and dropped on save.
 */
function FilterConfigSection({
  filters,
  columns,
  columnsByFieldUuid,
  columnsLoading,
  columnsError,
  onRetryColumns,
  onAdd,
  onChange,
  onRemove,
}) {
  const { t } = useTranslation('dashboards');

  const title = (
    <span className="inline-flex items-center gap-2">
      {t('widgetBuilder.filters.title')}
      {filters.length > 0 ? (
        <Badge size="small" variant="secondary">{filters.length}</Badge>
      ) : null}
    </span>
  );

  const body = () => {
    if (columnsError) {
      return (
        <PickerError
          title={t('widgetBuilder.filters.errorTitle')}
          subtitle={t('widgetBuilder.filters.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={onRetryColumns}
        />
      );
    }

    // A skeleton, not an empty select: an empty dropdown reads as "this source
    // has no columns", which is a different and wrong conclusion.
    if (columnsLoading && filters.length > 0) return <PickerSkeleton count={filters.length} height={40} />;

    // Empty carries its own `Add filter`, so the tertiary button below would be
    // a second one saying the same thing.
    if (filters.length === 0) {
      return (
        <FiltersEmptyState
          title={t('widgetBuilder.filters.emptyTitle')}
          subtitle={t('widgetBuilder.filters.empty')}
          addLabel={t('widgetBuilder.filters.add')}
          onAdd={onAdd}
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {filters.map((filter) => (
          <FilterConfigRow
            key={filter.id}
            filter={filter}
            columns={columns}
            columnsByFieldUuid={columnsByFieldUuid}
            columnsLoading={columnsLoading}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}

        <div>
          <Button variant="tertiary" size="sm" leftIcon={Plus} onClick={onAdd}>
            {t('widgetBuilder.filters.add')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <AccordionSection title={title} titleSize="xs" defaultOpen>
      <div className="pb-4 pt-3">{body()}</div>
    </AccordionSection>
  );
}

FilterConfigSection.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnsByFieldUuid: PropTypes.objectOf(PropTypes.object).isRequired,
  columnsLoading: PropTypes.bool,
  columnsError: PropTypes.object,
  onRetryColumns: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default FilterConfigSection;
